import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, Search, Edit, Archive, MoreHorizontal, Eye } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Link } from "react-router-dom";
function ProtectionPlanTemplates({ onCreateNew, onEditTemplate }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const mockTemplates = [
    {
      id: 1,
      planId: "TPL-2024-001",
      planTitle: "Premium Electronics Protection",
      planDescription:
        "Comprehensive coverage for high-end electronics including laptops, phones, and tablets",
      productTypes: ["Laptop", "Phone", "Tablet"],
      coverageType: "Premium",
      issueTypes: [
        { id: "liquid_damage", label: "Liquid Damage", icon: "droplets" },
        { id: "screen_crack", label: "Screen Damage", icon: "monitor" },
        { id: "hardware_failure", label: "Hardware Failure", icon: "cpu" },
      ],
      isActive: true,
      activePlansCount: 156,
      revenueThisMonth: 45200,
      createdAt: "2024-01-15T00:00:00Z",
      updatedAt: "2024-06-15T00:00:00Z",
    },
    {
      id: 2,
      planId: "TPL-2024-002",
      planTitle: "Basic Device Protection",
      planDescription: "Essential protection for everyday devices",
      productTypes: ["Phone", "Tablet"],
      coverageType: "Basic",
      issueTypes: [
        { id: "screen_crack", label: "Screen Damage", icon: "monitor" },
        { id: "battery_issue", label: "Battery Issues", icon: "battery" },
      ],
      isActive: true,
      activePlansCount: 89,
      revenueThisMonth: 12650,
      createdAt: "2024-02-01T00:00:00Z",
      updatedAt: "2024-05-20T00:00:00Z",
    },
    {
      id: 3,
      planId: "TPL-2024-003",
      planTitle: "Home Appliance Coverage",
      planDescription: "Extended warranty for kitchen and home appliances",
      productTypes: ["Appliance"],
      coverageType: "Extended",
      issueTypes: [
        {
          id: "mechanical_failure",
          label: "Mechanical Failure",
          icon: "settings",
        },
        { id: "electrical_issue", label: "Electrical Issues", icon: "zap" },
      ],
      isActive: true,
      activePlansCount: 203,
      revenueThisMonth: 28750,
      createdAt: "2024-03-10T00:00:00Z",
      updatedAt: "2024-06-01T00:00:00Z",
    },
    {
      id: 4,
      planId: "TPL-2023-008",
      planTitle: "Legacy Gaming Console Protection",
      planDescription: "Protection for older gaming consoles - deprecated",
      productTypes: ["Gaming"],
      coverageType: "Basic",
      issueTypes: [
        { id: "overheating", label: "Overheating", icon: "thermometer" },
      ],
      isActive: false,
      activePlansCount: 12,
      revenueThisMonth: 0,
      createdAt: "2023-08-15T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
  ];

  const filteredTemplates = mockTemplates.filter((template) => {
    const matchesSearch =
      template.planTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.planId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.productTypes.some((type) =>
        type.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && template.isActive) ||
      (statusFilter === "archived" && !template.isActive);

    const matchesType =
      typeFilter === "all" ||
      template.productTypes.some(
        (type) => type.toLowerCase() === typeFilter.toLowerCase()
      );

    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
        Active
      </Badge>
    ) : (
      <Badge variant="secondary">Archived</Badge>
    );
  };

  const getCoverageBadge = (coverageType) => {
    const colors = {
      Basic: "bg-blue-100 !text-blue-800",
      Extended: "bg-purple-100 text-purple-800",
      Premium: "bg-amber-100 text-amber-800",
    };

    return (
      <Badge
        className={`${colors[coverageType]} hover:${colors[coverageType]}`}
      >
        {coverageType}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const totalTemplates = mockTemplates.length;
  const activeTemplates = mockTemplates.filter((t) => t.isActive).length;
  const totalActivePlans = mockTemplates.reduce(
    (sum, t) => sum + t.activePlansCount,
    0
  );
  const totalRevenue = mockTemplates.reduce(
    (sum, t) => sum + t.revenueThisMonth,
    0
  );
  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Protection Plan Templates
            </h1>
            <p className="text-gray-600">
              Manage and monitor all protection plan templates
            </p>
          </div>
          <Link to="/create-protection-plan">
            <Button
              onClick={onCreateNew}
              className="bg-accent hover:bg-accent/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Template
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Total Templates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {totalTemplates}
              </div>
              <div className="text-xs text-green-600">
                {activeTemplates} active
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Active Plans
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {totalActivePlans.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Total plans in use</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Revenue This Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(totalRevenue)}
              </div>
              <div className="text-xs text-green-600">+12% from last month</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Avg Plans per Template
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {Math.round(totalActivePlans / activeTemplates)}
              </div>
              <div className="text-xs text-gray-500">Per active template</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filter Templates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search by template name, ID, or product type..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-full sm:w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-48">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="laptop">Laptop</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="tablet">Tablet</SelectItem>
                    <SelectItem value="appliance">Appliance</SelectItem>
                    <SelectItem value="gaming">Gaming</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Templates Table */}
        <Card>
          <CardHeader>
            <CardTitle>Templates ({filteredTemplates.length})</CardTitle>
            <CardDescription>
              Protection plan templates and their usage statistics
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template Details</TableHead>
                    <TableHead>Product Types</TableHead>
                    <TableHead>Coverage</TableHead>
                    <TableHead>Active Plans</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium text-gray-900">
                            {template.planTitle}
                          </div>
                          <div className="text-sm text-gray-500">
                            {template.planId}
                          </div>
                          <div className="text-xs text-gray-400 mt-1 max-w-xs truncate">
                            {template.planDescription}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {template.productTypes.map((type, index) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className="text-xs"
                            >
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getCoverageBadge(template.coverageType)}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {template.activePlansCount}
                        </div>
                        <div className="text-xs text-gray-500">
                          {template.issueTypes.length} issue types
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {formatCurrency(template.revenueThisMonth)}
                        </div>
                        <div className="text-xs text-gray-500">This month</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {formatDate(template.updatedAt)}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(template.isActive)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => onEditTemplate(template.planId)}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Template
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Archive className="h-4 w-4 mr-2" />
                              {template.isActive ? "Archive" : "Activate"}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {filteredTemplates.length === 0 && (
              <div className="text-center py-8">
                <div className="text-gray-500">
                  No templates found matching your criteria
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                    setTypeFilter("all");
                  }}
                  className="mt-2"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
export default ProtectionPlanTemplates;
